import React, { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './components/auth/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import './index.css';
import './App.css';
import api from "./api";
import NotificationListener from './components/notifications/NotificationListener';
// import UseInactivityLogout from './UseInactivityLogout';
import MessageNotification from './components/notifications/MessageNotification';
import InternetSpeedChecker from './InternetSpeedChecker';
import UserProfileAlert from './components/modal/UserProfileAlert';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import NetworkStatus from './components/notifications/NetworkStatus';
import BlockThirdPartyAd from './utils/BlockThirdPartyAd';
import useAutoReload from './hooks/useAutoReload';
import SuspenseWrapper from './SuspenseWrapper';
import AdScriptInjector from './pages/monetag/AdScriptInjector';
// import WebApp from '@twa-dev/sdk';


const queryClient = new QueryClient();
const Register = lazy(() => import('./pages/register/Register'));
const Login = lazy(() => import('./pages/login/Login'));
const Offline = lazy(() => import('./pages/login/Offline'));
const LoginHistory = lazy(() => import('./pages/loginhistory/LoginHistory'));
const ForgotPassword = lazy(() => import('./pages/login/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/login/ResetPassword'));
const Home = lazy(() => import('./pages/home/Home'));
const Dashboard = lazy(() => import('./pages/dashboard/Dashboard'));
const Campaigns = lazy(() => import('./pages/campaigns/Campaigns'));
const CampaignDetails = lazy(() => import('./pages/campaigns/CampaignDetails'));
const TotalCampaigns = lazy(() => import('./pages/campaigns/TotalCampaigns'));
const Matchmaker = lazy(() => import('./pages/matchmaker/Matchmaker'));
const CreateProfile = lazy(() => import('./pages/matchmaker/CreateProfile'));
const TotalMatches = lazy(() => import('./pages/matchmaker/TotalMatches'));
const SearchResults = lazy(() => import('./pages/matchmaker/SearchResults'));
const SyncMatch = lazy(() => import('./pages/matchmaker/SyncMatch'));
const Profile = lazy(() => import('./pages/profile/Profile'));
const ProfileDetails = lazy(() => import('./pages/profile/ProfileDetails'));
const Settings = lazy(() => import('./pages/settings/Settings'));
const Wallet = lazy(() => import('./pages/wallet/Wallet'));
const SellCoins = lazy(() => import('./pages/wallet/SellCoins'));
const Statistics = lazy(() => import('./pages/statistics/Statistics'));
const Voting = lazy(() => import('./pages/vote/Voting'));
const VotePrice = lazy(() => import('./pages/vote/VotePrice'));
const CompetitionManager = lazy(() => import('./pages/vote/CompetitionManager'));
const Message = lazy(() => import('./pages/messages/Message'));
const AboutUs = lazy(() => import('./pages/about/AboutUs'));
const Discovery = lazy(() => import('./pages/discovery/Discovery'));
const Tasks = lazy(() => import('./pages/tasks/Tasks'));
const Referral = lazy(() => import('./pages/referral/Referral'));
const Adverts = lazy(() => import('./pages/adverts/Adverts'));
const ViewedAdverts = lazy(() => import('./pages/adverts/ViewedAdverts'));
const GoogleAdverts = lazy(() => import('./pages/adverts/google/GoogleAdverts'));
const AdDetails = lazy(() => import('./pages/adverts/google/AdDetails'));
const CustomAdverts = lazy(() => import('./pages/adverts/custom/CustomAdverts'));
const Contact = lazy(() => import('./pages/contact/Contact'));
const UserList = lazy(() => import('./pages/messages/UserList'));
const ChatView = lazy(() => import('./pages/messages/ChatView'));
const BusinessAccount = lazy(() => import('./pages/business/BusinessAccount'));
const BusinessProfile = lazy(() => import('./pages/business/BusinessProfile'));
const BusinessSponsor = lazy(() => import('./pages/business/sponsor/BusinessSponsor'));
const ApprovedBusinessPartners = lazy(() => import('./pages/partners/ApprovedBusinessPartners'));
const PartnerDetails = lazy(() => import('./pages/partners/PartnerDetails'));
const ManagePartnerAccount = lazy(() => import('./pages/partners/ManagePartnerAccount'));
const Policies = lazy(() => import('./pages/policies/Policies'));
const PrivacyPolicy = lazy(() => import('./pages/policies/PrivacyPolicy'));
const TermsOfUse = lazy(() => import('./pages/policies/TermsOfUse'));
const CookiePolicy = lazy(() => import('./pages/policies/CookiePolicy'));
const Disclaimer = lazy(() => import('./pages/policies/Disclaimer'));
const AccessibilityStatement = lazy(() => import('./pages/policies/AccessibilityStatement'));
const CommunityGuidelines = lazy(() => import('./pages/policies/CommunityGuidelines'));
const CybercrimesAct = lazy(() => import('./pages/policies/CybercrimesAct'));
const RulesAndRegulations = lazy(() => import('./pages/policies/RulesAndRegulations'));
const DataProtection = lazy(() => import('./pages/policies/DataProtection'));
const BookList = lazy(() => import('./pages/reader/BookList'));
const AllAccounts = lazy(() => import('./pages/accounts/AllAccounts'));
const GamesHome = lazy(() => import('./pages/games/GamesHome'));
const Betting = lazy(() => import('./pages/games/betting/Betting'));
const TargetShooter = lazy(() => import('./pages/games/targetshooter/TargetShooter'));
const CosmicCoinFlip = lazy(() => import('./pages/games/cosmicCoinFlip/CosmicCoinFlip'));
const NotFound = lazy(() => import('./pages/notfound/NotFound'));
const OnlineUsers = lazy(() => import('./pages/users/OnlineUsers'));
const ServerInfo = lazy(() => import('./pages/admin/ServerInfo'));
const ServerDashboard = lazy(() => import('./pages/admin/ServerDashboard'));
const ManageUsers = lazy(() => import('./pages/admin/users/ManageUsers'));
// const ManageUsers = lazy(() => import('./pages/admin/users/ManageUsers1'));
const CoinsMining = lazy(() => import('./pages/mining/CoinsMining'));
const MonetagAd = lazy(() => import('./pages/monetag/MonetagAd'));
const ProfileAIEnhancer = lazy(() => import('./pages/profile/ProfileAIEnhancer'));
// const ProfileAIEnhancerAccount = lazy(() => import('./pages/profile/ProfileEnhancerModal'));
const TelegramMiniApp = lazy(() => import('./pages/telegram/TelegramMiniApp'));
const CoinEconomy = lazy(() => import('./pages/coineconomy/CoinEconomy'));
const MyDevice = lazy(() => import('./pages/mydevice/MyDevice'));
const AppOverview = lazy(() => import('./pages/overview/AppOverview'));
const Questions = lazy(() => import('./pages/questions/Questions'));
const AdminQuestionsDashboard = lazy(() => import('./pages/questions/AdminQuestionsDashboard'));
const Voice = lazy(() => import('./pages/voice/Voice'));
const PaymentCallback = lazy(() => import('./pages/paymentCallback/PaymentCallback'));


const Faq = lazy(() => import('./pages/faq/Faq'));



const App = () => {
  useAutoReload(); // Automatically reload if a blank page is detected
  
  const [userId, setUserId] = useState(() => {
    // Initial userId state from session storage
    const user = JSON.parse(sessionStorage.getItem("user")) || null;
    return user?.user_id || 0;
  });

  // Watch for changes in session storage and update userId
  useEffect(() => {
    const handleStorageChange = () => {
      const user = JSON.parse(sessionStorage.getItem("user")) || null;
      const newUserId = user?.user_id || 0;
      setUserId(newUserId); // Update state with new userId
    };

    // Listen for storage events
    window.addEventListener("storage", handleStorageChange);

    // Cleanup listener on component unmount
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

    // useEffect(() => {
    //   // Initialize Telegram WebApp
    //   WebApp.ready();
      
    //   // Expand the app to full view (optional)
    //   WebApp.expand();
    // }, []);
  
    // const handleClose = () => {
    //   WebApp.close();
    // };


  return (
    <AuthProvider>
      <QueryClientProvider client={queryClient}>
        <NetworkStatus />
        {/* <BlockThirdPartyAd /> */}

        <BrowserRouter>
          {/* <UserProfileAlert userId={userId} /> */}
          <Routes>
            <Route path="/" element={<SuspenseWrapper><Home /></SuspenseWrapper>} />
            <Route path="/register" element={<SuspenseWrapper><Register /></SuspenseWrapper>} />
            <Route path="/login" element={<SuspenseWrapper><Login /></SuspenseWrapper>} />
            <Route path="/offline" element={<Offline />} />

            {/* the policies of the app, they are not protected */}
            <Route path="/policies" element={<SuspenseWrapper><Policies /></SuspenseWrapper>} />
            <Route path="/privacy-policy" element={<SuspenseWrapper><PrivacyPolicy /></SuspenseWrapper>} />
            <Route path="/terms-of-use" element={<SuspenseWrapper><TermsOfUse /></SuspenseWrapper>} />
            <Route path="/cookie-policy" element={<SuspenseWrapper><CookiePolicy /></SuspenseWrapper>} />
            <Route path="/disclaimer" element={<SuspenseWrapper><Disclaimer /></SuspenseWrapper>} />
            <Route path="/accessibility-statement" element={<SuspenseWrapper><AccessibilityStatement /></SuspenseWrapper>} />
            <Route path="/community-guidelines" element={<SuspenseWrapper><CommunityGuidelines /></SuspenseWrapper>} />
            <Route path="/cyber-crimes-act" element={<SuspenseWrapper><CybercrimesAct /></SuspenseWrapper>} />
            <Route path="/rules-and-regulations" element={<SuspenseWrapper><RulesAndRegulations /></SuspenseWrapper>} />
            <Route path="/data-protection" element={<SuspenseWrapper><DataProtection /></SuspenseWrapper>} />




            <Route path="/forgot-password" element={<SuspenseWrapper><ForgotPassword /></SuspenseWrapper>} />
            <Route path="/reset-password" element={<SuspenseWrapper><ResetPassword /></SuspenseWrapper>} />
            <Route path="/overview" element={<SuspenseWrapper><AppOverview /></SuspenseWrapper>} />
            




            {/* these are protected routes, a user must be logged in before they access it */}
         
            <Route path="/login-history" element={<ProtectedRoute element={<SuspenseWrapper><LoginHistory /></SuspenseWrapper>} />} />
            <Route path="/dashboard" element={<ProtectedRoute element={<SuspenseWrapper><Dashboard /></SuspenseWrapper>} />} />
            <Route path="/campaigns" element={<ProtectedRoute element={<SuspenseWrapper><Campaigns /></SuspenseWrapper>} />} />
            <Route path="/matchmaker" element={<ProtectedRoute element={<SuspenseWrapper><Matchmaker /></SuspenseWrapper>} />} />
            <Route path="/search-results" element={<ProtectedRoute element={<SuspenseWrapper><SearchResults /></SuspenseWrapper>} />} />
            <Route path="/sync-match" element={<ProtectedRoute element={<SuspenseWrapper><SyncMatch /></SuspenseWrapper>} />} />
            <Route path="/create-profile" element={<ProtectedRoute element={<SuspenseWrapper><CreateProfile /></SuspenseWrapper>} />} />
            <Route path="/total-matches" element={<ProtectedRoute element={<SuspenseWrapper><TotalMatches /></SuspenseWrapper>} />} />
            <Route path="/total-campaigns" element={<ProtectedRoute element={<SuspenseWrapper><TotalCampaigns /></SuspenseWrapper>} />} />
            <Route path="/campaigns/:username/:campaignCode" element={<ProtectedRoute element={<SuspenseWrapper><CampaignDetails /></SuspenseWrapper>} />} />
            <Route path="/settings" element={<ProtectedRoute element={<SuspenseWrapper><Settings /></SuspenseWrapper>} />} />
            <Route path="/wallet" element={<ProtectedRoute element={<SuspenseWrapper><Wallet /></SuspenseWrapper>} />} />
            <Route path="/sell-coins" element={<ProtectedRoute element={<SuspenseWrapper><SellCoins /></SuspenseWrapper>} />} />
            <Route path="/statistics" element={<ProtectedRoute element={<SuspenseWrapper><Statistics /></SuspenseWrapper>} />} />
            
            <Route path="/vote" element={<ProtectedRoute element={<SuspenseWrapper><Voting /></SuspenseWrapper>} />} />
            <Route path="/vote-price" element={<ProtectedRoute element={<SuspenseWrapper><VotePrice /></SuspenseWrapper>} />} />
            <Route path="/competition-manager" element={<ProtectedRoute element={<SuspenseWrapper><CompetitionManager /></SuspenseWrapper>} />} />

            


            <Route path="/message" element={<ProtectedRoute element={<SuspenseWrapper><Message /></SuspenseWrapper>} />} />
            <Route path="/about" element={<ProtectedRoute element={<SuspenseWrapper><AboutUs /></SuspenseWrapper>} />} />
            <Route path="/discoveries" element={<ProtectedRoute element={<SuspenseWrapper><Discovery /></SuspenseWrapper>} />} />
            <Route path="/tasks" element={<ProtectedRoute element={<SuspenseWrapper><Tasks /></SuspenseWrapper>} />} />
            <Route path="/referral" element={<ProtectedRoute element={<SuspenseWrapper><Referral /></SuspenseWrapper>} />} />
            <Route path="/adverts" element={<ProtectedRoute element={<SuspenseWrapper><Adverts /></SuspenseWrapper>} />} />
            <Route path="/viewed-adverts" element={<ProtectedRoute element={<SuspenseWrapper><ViewedAdverts /></SuspenseWrapper>} />} />

            <Route path="/google-adverts" element={<ProtectedRoute element={<SuspenseWrapper>
              <AdScriptInjector /> {/* Ads load only here */}
              <GoogleAdverts />
              </SuspenseWrapper>} />} />


            <Route path="/ad-details/:id" element={<ProtectedRoute element={<SuspenseWrapper><AdDetails /></SuspenseWrapper>} />} />
            <Route path="/custom-adverts" element={<ProtectedRoute element={<SuspenseWrapper><CustomAdverts /></SuspenseWrapper>} />} />



            <Route path="/contact" element={<ProtectedRoute element={<SuspenseWrapper><Contact /></SuspenseWrapper>} />} />
            <Route path="/users-messages" element={<ProtectedRoute element={<SuspenseWrapper><UserList /></SuspenseWrapper>} />} />
            <Route path="/chat/:receiverId" element={<ProtectedRoute element={<SuspenseWrapper><ChatView /></SuspenseWrapper>} />} />
            <Route path="/business-account" element={<ProtectedRoute element={<SuspenseWrapper><BusinessAccount /></SuspenseWrapper>} />} />
            <Route path="/business-profile/:id" element={<ProtectedRoute element={<SuspenseWrapper><BusinessProfile /></SuspenseWrapper>} />} />

            <Route path="/business-sponsor" element={<ProtectedRoute element={<SuspenseWrapper><BusinessSponsor /></SuspenseWrapper>} />} />

            
            <Route path="/approved-business-partners" element={<ProtectedRoute element={<SuspenseWrapper><ApprovedBusinessPartners /></SuspenseWrapper>} />} />
            <Route path="/business-partner/:id" element={<ProtectedRoute element={<SuspenseWrapper><PartnerDetails /></SuspenseWrapper>} />} />
            <Route path="/manage-partner-account" element={<ProtectedRoute element={<SuspenseWrapper><ManagePartnerAccount /></SuspenseWrapper>} />} />
            <Route path="/discoveries/:username" element={<ProtectedRoute element={<SuspenseWrapper><ProfileDetails /></SuspenseWrapper>} />} />


            <Route path="/book-list" element={<ProtectedRoute element={<SuspenseWrapper><BookList /></SuspenseWrapper>} />} />
            <Route path="/all-accounts" element={<ProtectedRoute element={<SuspenseWrapper><AllAccounts /></SuspenseWrapper>} />} />

            

            <Route path="/games-home" element={<ProtectedRoute element={<SuspenseWrapper><GamesHome /></SuspenseWrapper>} />} />
            <Route path="/betting" element={<ProtectedRoute element={<SuspenseWrapper><Betting /></SuspenseWrapper>} />} />
            <Route path="/targetshooter" element={<ProtectedRoute element={<SuspenseWrapper><TargetShooter /></SuspenseWrapper>} />} />
            <Route path="/cosmic-coin-flip" element={<ProtectedRoute element={<SuspenseWrapper><CosmicCoinFlip /></SuspenseWrapper>} />} />

            

            <Route path="/online-logged-in-users" element={<ProtectedRoute element={<SuspenseWrapper><OnlineUsers /></SuspenseWrapper>} />} />


            <Route path="/backend-server-info" element={<ProtectedRoute element={<SuspenseWrapper><ServerInfo /></SuspenseWrapper>} />} />

            <Route path="/server-dashboard" element={<ProtectedRoute element={<SuspenseWrapper><ServerDashboard /></SuspenseWrapper>} />} />

            <Route path="/manage-users" element={<ProtectedRoute element={<SuspenseWrapper><ManageUsers /></SuspenseWrapper>} />} />

            <Route path="/coins-mining" element={<ProtectedRoute element={<SuspenseWrapper><CoinsMining /></SuspenseWrapper>} />} />

            <Route path="/monetag-ad" element={<ProtectedRoute element={<SuspenseWrapper><MonetagAd /></SuspenseWrapper>} />} />


            <Route path="/ai-enhancer" element={<ProtectedRoute element={<SuspenseWrapper><ProfileAIEnhancer /></SuspenseWrapper>} />} />



            <Route path="/telegram-mini-app" element={<ProtectedRoute element={<SuspenseWrapper><TelegramMiniApp /></SuspenseWrapper>} />} />


            <Route path="/coin-economy" element={<ProtectedRoute element={<SuspenseWrapper><CoinEconomy /></SuspenseWrapper>} />} />


            <Route path="/my-device" element={<ProtectedRoute element={<SuspenseWrapper><MyDevice /></SuspenseWrapper>} />} />



               
            <Route path="/questions" element={<ProtectedRoute element={<SuspenseWrapper><Questions /></SuspenseWrapper>} />} />
            <Route path="/admin-questions-dashboard" element={<ProtectedRoute element={<SuspenseWrapper><AdminQuestionsDashboard /></SuspenseWrapper>} />} />


            <Route path="/voice" element={<ProtectedRoute element={<SuspenseWrapper><Voice /></SuspenseWrapper>} />} />
            
            <Route path="/payment-callback" element={<ProtectedRoute element={<SuspenseWrapper><PaymentCallback /></SuspenseWrapper>} />} />


            <Route path="/faq" element={<ProtectedRoute element={<SuspenseWrapper><Faq /></SuspenseWrapper>} />} />
            


            {/* Redirect to login for undefined routes */}
            {/* <Route path="*" element={<Navigate to="/login" replace />} /> */}
            <Route path="*" element={<SuspenseWrapper><NotFound /></SuspenseWrapper>} />
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>

    </AuthProvider>
  );
};

export default App;